/**
 * SavedCaseNotice — the Merlin chat's confirmation after "Save as
 * investigation" (proposal 8): "Saved as case INV-00007 · Open the case" or
 * "Added to INV-00004 · Open the case · Undo". Undo takes the conversation
 * back off the case and is offered for 30 seconds, long enough to catch a
 * wrong pick, short enough that nobody undoes someone else's later work.
 * A new case offers "Have Merlin fill it in" instead (UX loop turn 2).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { failedActionMessage } from './investigationsView';
import {
  detachInvestigationConversation,
  type DoorInvestigationRead,
} from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import type { SaveOutcome } from './SaveAsInvestigationMenu';
import styles from './SavedCaseNotice.module.css';

export const UNDO_WINDOW_MS = 30_000;

export interface SavedCaseNoticeProps {
  investigation: Pick<DoorInvestigationRead, 'id' | 'investigation_no'>;
  conversationId: string;
  outcome: SaveOutcome;
  /** Route to the case (relative to the door). */
  caseHref: string;
  /** "Have Merlin fill it in" (new cases only; hidden once asked). */
  fillOffered: boolean;
  fillDisabled: boolean;
  onFill: () => void;
  /** The conversation is off the case again (the detach response). */
  onUndone: (investigation: DoorInvestigationRead) => void;
}

export function SavedCaseNotice({
  investigation,
  conversationId,
  outcome,
  caseHref,
  fillOffered,
  fillDisabled,
  onFill,
  onUndone,
}: SavedCaseNoticeProps) {
  const [undoOpen, setUndoOpen] = useState(outcome === 'attach');
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const no = investigation.investigation_no;

  // The Save button is gone (the header now says "part of INV-…"), so focus
  // lands on the confirmation rather than on the page body.
  useEffect(() => {
    noticeRef.current?.focus();
  }, [investigation.id]);

  useEffect(() => {
    if (outcome !== 'attach') return;
    setUndoOpen(true);
    const timer = window.setTimeout(() => setUndoOpen(false), UNDO_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [outcome, investigation.id, conversationId]);

  const undo = async () => {
    if (undoing) return;
    setUndoing(true);
    setError(null);
    try {
      const updated = await detachInvestigationConversation(investigation.id, conversationId);
      trackEvent('action', 'save_dialog_undo', {
        conversation_id: conversationId,
        investigation_id: investigation.id,
        investigation_no: no,
      });
      onUndone(updated);
    } catch (e) {
      setError(failedActionMessage(`take this conversation off ${no}`, e));
    } finally {
      setUndoing(false);
    }
  };

  return (
    <p className={styles.notice} role="status" ref={noticeRef} tabIndex={-1}>
      {outcome === 'attach' ? (
        <>
          ✓ Added to <b>{no}</b>
        </>
      ) : (
        <>
          ✓ Saved as case <b>{no}</b>
        </>
      )}
      <span className={styles.sep} aria-hidden="true">·</span>
      <Link
        to={caseHref}
        onClick={() =>
          trackEvent('click', 'save_notice_open_case', {
            conversation_id: conversationId,
            investigation_no: no,
            outcome,
          })
        }
      >
        Open the case
      </Link>
      {outcome === 'attach' && undoOpen && (
        <>
          <span className={styles.sep} aria-hidden="true">·</span>
          <button
            type="button"
            className={styles.linkBtn}
            onClick={() => void undo()}
            disabled={undoing}
          >
            {undoing ? 'Undoing…' : 'Undo'}
          </button>
        </>
      )}
      {outcome === 'new' && fillOffered && (
        <button type="button" className={styles.fillBtn} disabled={fillDisabled} onClick={onFill}>
          Have Merlin fill it in
        </button>
      )}
      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </p>
  );
}

export default SavedCaseNotice;
